import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook that forces table remount when transitioning from empty filtered state to data state.
 *
 * ## Context
 *
 * This hook was created to solve a React rendering bug where tables would not display data
 * after clearing a search/filter that returned no results. The issue manifested across
 * multiple table implementations (Assets, Technologies, Vulnerabilities, Seeds).
 *
 * ## Root Cause
 *
 * When a user searches for non-existent data and then clears the search, React's reconciliation
 * algorithm sometimes fails to re-render the virtualized table component, even when the data
 * array transitions from empty to populated. This appears to be related to stale refs/closures
 * within the virtualization library (TanStack Virtual) that don't update properly.
 *
 * ## Solution
 *
 * By changing the `key` prop on the table component when transitioning from empty → has data,
 * we force React to unmount the old table instance and mount a fresh one. This:
 * - Destroys and recreates the entire component tree
 * - Resets all internal state and refs
 * - Forces the virtualizer to initialize with the current data
 * - Triggers a complete re-render with fresh closures
 *
 * ## Race Condition Handling
 *
 * The hook uses `hadEmptyState` ref to track if we've EVER been in an empty state, rather than
 * just comparing previous vs. current state. This prevents a race condition where:
 * 1. User searches → no results (hadEmptyState: true)
 * 2. User clears search BEFORE query completes
 * 3. Old query finishes → isFilteredEmpty: true, hadEmptyState: true
 * 4. New query starts → isFilteredEmpty: false, hadEmptyState: STAYS TRUE (preserved!)
 * 5. New query finishes → remount triggers correctly
 *
 * Without this, step 4 would reset tracking and remount would never trigger.
 *
 * ## Trade-offs
 *
 * **Benefits:**
 * - ✅ Fixes the stuck table rendering bug
 * - ✅ Handles race conditions during rapid filter changes
 * - ✅ Minimal code changes required
 * - ✅ Preserves normal operation (only remounts in edge case)
 *
 * **Limitations:**
 * - ❌ Loses scroll position during remount (acceptable for this edge case)
 * - ❌ Loses row selection during remount (acceptable for this edge case)
 * - ❌ Brief UI flicker during remount (minimal impact)
 *
 * ## Related Documentation
 *
 * - Original analysis: `/search-bug/FORCE-REMOUNT-APPROACH.md`
 * - Implementation pattern: `/search-bug/ASSETS-FIX-IMPLEMENTATION.md` (if exists)
 *
 * @param isFilteredEmpty - Whether the current filters return no results
 * @param dataLength - Current length of the data array
 * @returns A stable key that changes only when transitioning from empty → has data
 *
 * @example
 * ```tsx
 * // In a table component
 * function TechnologiesSection() {
 *   const { data: technologies, isFilteredEmpty } = useGetTechnologies();
 *
 *   const tableKey = useTableRemountOnTransition(isFilteredEmpty, technologies.length);
 *
 *   return (
 *     <TechnologyTable
 *       key={tableKey}
 *       data={technologies}
 *       {...otherProps}
 *     />
 *   );
 * }
 * ```
 */
export function useTableRemountOnTransition(
  isFilteredEmpty: boolean,
  dataLength: number
): number {
  const [tableKey, setTableKey] = useState(0);
  const hadEmptyState = useRef(false);

  useEffect(() => {
    // Track if we've EVER been in empty state
    if (isFilteredEmpty) {
      hadEmptyState.current = true;
    }

    // Remount when transitioning from empty → has data
    const isNowNotEmpty = !isFilteredEmpty && dataLength > 0;

    if (hadEmptyState.current && isNowNotEmpty) {
      setTableKey(k => k + 1);
      hadEmptyState.current = false; // Reset after remount
    }
  }, [isFilteredEmpty, dataLength]);

  return tableKey;
}
